import React, { Fragment, useEffect, useCallback, useRef } from "react";
import { Transition } from "@headlessui/react";
import "./SideDrawer.css";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

const SideDrawer = ({
  isOpen,
  onClose,
  position = "left",
  title,
  width = "400px",
  showOverlay = true,
  headerIcon,
  children,
  testId = "side-drawer",
}) => {
  const { t } = useTranslation();
  const panelRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.defaultPrevented) {
        return;
      }

      if (e.key === "Escape" && isOpen) {
        onClose();
        return;
      }

      if (e.key !== "Tab" || !isOpen || !panelRef.current) {
        return;
      }

      const focusableElements = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (!firstFocusable || !lastFocusable) {
        e.preventDefault();
        panelRef.current.focus();
        return;
      }

      if (e.shiftKey && document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      } else if (!e.shiftKey && document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    },
    [isOpen, onClose],
  );

  useEffect(() => {
    if (isOpen) {
      previouslyFocusedElementRef.current = document.activeElement;
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", handleKeyDown);
      setTimeout(() => {
        if (closeButtonRef.current) {
          closeButtonRef.current.focus();
          return;
        }
        panelRef.current?.focus();
      }, 0);
    }
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleKeyDown);
      if (isOpen) {
        previouslyFocusedElementRef.current?.focus?.();
      }
    };
  }, [isOpen, handleKeyDown]);

  const isLeft = position === "left";
  const hiddenTransform = isLeft ? "translateX(-100%)" : "translateX(100%)";
  const visibleTransform = "translateX(0)";

  return (
    <>
      {showOverlay && (
        <Transition
          appear
          show={isOpen}
          as={Fragment}
          enter="transition-opacity duration-300 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-200 ease-in"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="side-drawer-overlay"
            onClick={onClose}
            aria-hidden="true"
            id={`${testId}-overlay`}
          />
        </Transition>
      )}

      <div
        ref={panelRef}
        id={testId}
        className={`side-drawer-panel side-drawer-panel--${position}`}
        style={{
          width,
          transform: isOpen ? visibleTransform : hiddenTransform,
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          visibility: isOpen ? "visible" : "hidden",
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? `${testId}-title` : undefined}
        aria-label={title ? undefined : t("workspace:side_drawer")}
        aria-hidden={!isOpen}
        tabIndex={-1}
      >
        {title && (
          <div className="side-drawer-header">
            <div className="side-drawer-header__left">
              {headerIcon || <Info className="side-drawer-header__icon" />}
              <h3
                className="side-drawer-header__title"
                id={`${testId}-title`}
              >
                {title}
              </h3>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              id={`${testId}-close`}
              className="side-drawer-header__close"
              onClick={onClose}
              aria-label={t("workspace:close_drawer")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 4L4 12M4 4l8 8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
        <div className="side-drawer-body" id={`${testId}-body`}>
          {children}
        </div>
      </div>
    </>
  );
};

export default SideDrawer;
